// S2-LIB2: TabSwitcher — accessible segmented-control / pill-tab component.
// Matches the `.tab-switcher` / `.tab-switcher-tab` visual language used on
// the LLM Control Center (wireframe: docs/sprint-2/wireframes/s2-cc6-hero-and-section3.html).
// Used to switch between mutually exclusive views (e.g. Model / Eval Type,
// or time ranges). Implements the WAI-ARIA roving-tabindex tab pattern.
import { useRef, useCallback, KeyboardEvent } from 'react';
import './TabSwitcher.css';

// Each tab supplies a stable id (used for activeId / onChange) and a
// human-readable label. Kept intentionally minimal — no icons, no counts —
// so the component stays a pure segmented-control primitive.
export interface TabSwitcherTab {
  id: string;
  label: string;
}

export interface TabSwitcherProps {
  tabs: TabSwitcherTab[];
  activeId: string;
  onChange: (id: string) => void;
  // Visual size. Default 'sm' matches the compact pill tabs used in Section 3
  // of the LLM Control Center. 'md' bumps padding/font-size for larger surfaces.
  size?: 'sm' | 'md';
  // Accessible name for the tablist — screen readers announce this. Default
  // covers the common case; override for e.g. "Time range" or "Metric type".
  ariaLabel?: string;
}

/**
 * TabSwitcher — pill-style segmented tab control with full keyboard support.
 *
 * Why roving tabindex (not tabindex=0 on every tab): the WAI-ARIA tab pattern
 * lets users Tab INTO the tablist once, then use arrow keys to move between
 * tabs. This avoids forcing keyboard users through N tab stops for one widget.
 *
 * Activation follows focus via arrow keys (common for segmented controls) —
 * onChange fires immediately, and we programmatically focus the newly-active
 * button so Tab moves the user out of the widget naturally.
 */
export function TabSwitcher({
  tabs,
  activeId,
  onChange,
  size = 'sm',
  ariaLabel = 'Tab selector',
}: TabSwitcherProps) {
  // Ref array of the tab button DOM nodes — used to move focus programmatically
  // when arrow / Home / End keys change the active tab.
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // Locate the currently-active tab's index. -1 if activeId doesn't match any
  // tab (defensive; keyboard handlers treat -1 as "start from 0").
  const activeIndex = tabs.findIndex((t) => t.id === activeId);

  // Activate the tab at `nextIndex`: call onChange with its id, then move DOM
  // focus to its button so the user can Tab out of the widget. Memoized so
  // it's stable across renders (React strict-mode friendly).
  const activateIndex = useCallback(
    (nextIndex: number) => {
      const tab = tabs[nextIndex];
      if (!tab) return;
      onChange(tab.id);
      // Focus AFTER onChange — React may re-render, but querying the ref is
      // DOM-level so the button reference is still valid.
      buttonRefs.current[nextIndex]?.focus();
    },
    [tabs, onChange],
  );

  // Keyboard handler on the tablist. Arrow left/right wrap; Home/End jump
  // to ends; Enter/Space activate the currently-focused tab.
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      // Use the currently-active index as the "cursor" for arrow nav. If
      // nothing is active yet, start from 0.
      const current = activeIndex === -1 ? 0 : activeIndex;
      const last = tabs.length - 1;

      switch (event.key) {
        case 'ArrowRight': {
          event.preventDefault();
          // Wrap: last tab → first tab.
          const next = current === last ? 0 : current + 1;
          activateIndex(next);
          break;
        }
        case 'ArrowLeft': {
          event.preventDefault();
          // Wrap: first tab → last tab.
          const prev = current === 0 ? last : current - 1;
          activateIndex(prev);
          break;
        }
        case 'Home': {
          event.preventDefault();
          activateIndex(0);
          break;
        }
        case 'End': {
          event.preventDefault();
          activateIndex(last);
          break;
        }
        case 'Enter':
        case ' ': {
          // Space / Enter on the focused tab activates it. Figure out which
          // tab the user has focused (document.activeElement may be any of
          // the buttons; we rely on the data-index attribute).
          const target = event.target as HTMLElement;
          const indexAttr = target.getAttribute('data-index');
          if (indexAttr !== null) {
            event.preventDefault();
            activateIndex(Number(indexAttr));
          }
          break;
        }
        default:
          break;
      }
    },
    [activeIndex, tabs.length, activateIndex],
  );

  // Container class list — size modifier only added when non-default so the
  // base styles stay as the canonical pill tab look.
  const containerClassName = [
    'airgent-tab-switcher',
    `airgent-tab-switcher-${size}`,
  ].join(' ');

  return (
    <div
      className={containerClassName}
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
    >
      {tabs.map((tab, index) => {
        const isActive = tab.id === activeId;
        // Roving tabindex: exactly one tab is 0 (the active one), rest are -1.
        // This keeps the widget a single Tab stop.
        return (
          <button
            key={tab.id}
            ref={(el) => {
              buttonRefs.current[index] = el;
            }}
            type="button"
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            data-index={index}
            className={[
              'airgent-tab-switcher-tab',
              isActive ? 'airgent-tab-switcher-tab-active' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            // Pointer click activates directly — no focus shuffle needed
            // because clicking an element naturally gives it focus.
            onClick={() => {
              if (!isActive) onChange(tab.id);
            }}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
